import { useState } from 'react'
import { BookOpen, ChevronDown, ChevronRight } from 'lucide-react'
import Markdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import type { Components } from 'react-markdown'

interface SkillCardProps {
  /** Generated app name, shown in the header. */
  appName: string
  /** End-user SKILL.md (markdown) that the live decomposer ships with the app. */
  skill: string
}

/**
 * Renders the per-app end-user skill — the operating guide the live decomposer
 * ships alongside a generated app — inside the chat message stream. The skill is
 * markdown; it is rendered as real elements (headings, lists, tables) rather than
 * raw source so a creator can actually read how to operate what chat produced.
 *
 * Kept dependency-light and scoped: the markdown component map carries its own
 * Tailwind classes so the card does not rely on the `@tailwindcss/typography`
 * (`prose`) plugin, which the Studio Tailwind v4 build does not enable.
 */
const skillMarkdownComponents: Components = {
  h1: ({ children }) => <h1 className="mt-3 mb-1 text-sm font-semibold text-foreground first:mt-0">{children}</h1>,
  h2: ({ children }) => <h2 className="mt-3 mb-1 text-[13px] font-semibold text-foreground first:mt-0">{children}</h2>,
  h3: ({ children }) => <h3 className="mt-2 mb-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground first:mt-0">{children}</h3>,
  p: ({ children }) => <p className="my-1 text-[13px] leading-[1.5] text-foreground/90">{children}</p>,
  ul: ({ children }) => <ul className="my-1 list-disc space-y-0.5 pl-5 text-[13px] leading-[1.5] text-foreground/90">{children}</ul>,
  ol: ({ children }) => <ol className="my-1 list-decimal space-y-0.5 pl-5 text-[13px] leading-[1.5] text-foreground/90">{children}</ol>,
  li: ({ children }) => <li className="marker:text-muted-foreground">{children}</li>,
  strong: ({ children }) => <strong className="font-semibold text-foreground">{children}</strong>,
  em: ({ children }) => <em className="italic">{children}</em>,
  a: ({ href, children }) => (
    <a href={href} target="_blank" rel="noopener noreferrer" className="text-foreground underline underline-offset-2">
      {children}
    </a>
  ),
  code: ({ className, children }) => {
    // Inline code has no language class; fenced blocks carry `language-*`.
    if (/language-/.test(String(className ?? ''))) {
      return <code className="font-mono text-xs">{children}</code>
    }
    return <code className="rounded bg-muted px-1 py-0.5 font-mono text-[11px] text-foreground">{children}</code>
  },
  pre: ({ children }) => (
    <pre className="my-1.5 overflow-x-auto rounded-md bg-muted p-2 text-xs leading-snug">{children}</pre>
  ),
  blockquote: ({ children }) => (
    <blockquote className="my-1.5 border-l-2 border-border pl-3 text-[13px] italic text-muted-foreground">{children}</blockquote>
  ),
  table: ({ children }) => (
    <div className="my-1.5 overflow-x-auto">
      <table className="w-full border-collapse text-[12px]">{children}</table>
    </div>
  ),
  th: ({ children }) => <th className="border border-border bg-muted/50 px-2 py-1 text-left font-semibold">{children}</th>,
  td: ({ children }) => <td className="border border-border px-2 py-1 align-top">{children}</td>,
  hr: () => <hr className="my-2 border-border" />,
}

export function SkillCard({ appName, skill }: SkillCardProps) {
  const [expanded, setExpanded] = useState(false)
  const Chevron = expanded ? ChevronDown : ChevronRight

  return (
    <div
      className="my-1 w-full overflow-hidden rounded-lg border border-border/70 bg-card/80"
      data-testid="chatrail-app-skill-card"
    >
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        aria-expanded={expanded}
        data-testid="chatrail-app-skill-header"
        className={`flex w-full items-center gap-2 px-3 py-2 text-left transition-colors hover:bg-muted/50 ${
          expanded ? 'border-b border-border/60 bg-muted/35' : 'bg-transparent'
        }`}
      >
        <BookOpen className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />
        <span className="min-w-0 flex-1 truncate text-[13px] font-semibold text-foreground">
          {appName} 도움말
        </span>
        <Chevron className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />
      </button>

      {expanded ? (
        <div className="px-3 py-2" data-testid="chatrail-app-skill-body">
          <Markdown remarkPlugins={[remarkGfm]} components={skillMarkdownComponents}>
            {skill}
          </Markdown>
        </div>
      ) : null}
    </div>
  )
}
